/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useEffect, useMemo } from "react";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react";
import { columns as baseColumns, Internet, shapeInternetData } from "./InternetData";
import {
  getInternetServices,
  getInternetServiceById,
  addInternetService,
  updateInternetService,
  deleteInternetService,
  AddInternetPayload,
  UpdateInternetPayload,
} from "./request/request";

// UI components
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";

// Icons
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";

import { useAppSelector } from "@redux/hooks";
import { buildPredicate, FilterField, labelId, ParsedFilter as OldParsed, parseToken } from "../../../../helpers/advancedFilters";
import { CreateForm } from "./InternetModals";
import { InternetViewModal } from "./Modals/InternetViewModal";
import { InternetEditModal } from "./Modals/InternetEditModal";
import { InternetCreateModal } from "./Modals/InternetCreateModal";
import { useInternetForm, InternetFormData } from "./hooks/useInternetForm";
import {
  bgpConfigForPayload,
  bgpConfigFromApi,
  p2pAddressingForPayload,
  p2pAddressingFromApi,
} from "./internetAddressing";
import { getAllPorts, getKEPorts } from "../../Port/request/request";

export default function InternetRMPage() {
  // ========= 1) Table data =========
  const [internetServices, setInternetServices] = useState<Internet[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // ========= 2) Add/Edit modal state & fields =========
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<{ name: string; mbps: number | null }>({
    name: "",
    mbps: null,
  });
  const [originalEditValues, setOriginalEditValues] = useState<{ name: string; mbps: number | null }>({
    name: "",
    mbps: null,
  });
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedInternetId, setSelectedInternetId] = useState<number | null>(null);

  // Internet form state management using custom hook
  const internetForm = useInternetForm();

  // Keep createForm for edit modal compatibility (will be removed later)
  const [createForm, setCreateForm] = useState<CreateForm>({
    kontrolEdgePortId: null,
    mbps: null,
    vlan: null,
    automaticVlan: null,
    redundancy: null,
    countryCode: "",
    routing: "",
    terms: null,
    autoRenew: null,
    p2pAddressing: {
      mask: null,
      peerIp: "",
      gateway: "",
      network: "",
    },
    subscriptionId: "",
    name: "",
    tag: [],
  });

  // Port options for dropdown
  const [portOptions, setPortOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [selectedPortId, setSelectedPortId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // ========= 3) View modal state =========
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInternet, setSelectedInternet] = useState<Internet | null>(null);
  const [isViewLoading, setIsViewLoading] = useState(false);

  // ========= 4) Delete confirmation state =========
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [internetToDelete, setInternetToDelete] = useState<Internet | null>(null);

  // ========== Auth0 ==========
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // ========== Filters ==========
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);

  // ------------------------------------------------------------------------------------
  // useEffect: Fetch Internet services on mount
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    const fetchInternetServices = async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const response = await getInternetServices(token);
        const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
        const shapedData = shapeInternetData(raw);
        setInternetServices(shapedData);
      } catch (err) {
        console.error("Error fetching Internet services:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchInternetServices();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ------------------------------------------------------------------------------------
  // Filter logic
  // ------------------------------------------------------------------------------------
  function makeInternetFields(): FilterField<Internet>[] {
    return [
      { label: "Name", key: "name", type: "string", accessor: (r) => r.name, inFreeText: true },
      { label: "Subscription ID", key: "subscriptionid", type: "string", accessor: (r) => r.subscriptionId, inFreeText: true },
      { label: "Status", key: "status", type: "string", accessor: (r) => (r.isDraft ? "Draft" : "Active"), inFreeText: true },
    ];
  }

  const internetFields = useMemo(() => makeInternetFields(), []);
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Internet>>();
    for (const f of internetFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [internetFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of internetFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [internetFields]);

  const normalizedFilters = useMemo<ParsedFilter[]>(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":";
        }
      }

      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Internet>(normalizedFilters, internetFields),
    [normalizedFilters, internetFields]
  );

  const filteredData = useMemo(
    () => internetServices.filter(predicate),
    [internetServices, predicate]
  );

  const suggestedKeys = useMemo(() => {
    const headers = baseColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [baseColumns, isViewingAllCustomers]);

  // ------------------------------------------------------------------------------------
  // Fetch ports for dropdown
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    const fetchPorts = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await getKEPorts(token);
        // getKEPorts returns { success: true, message: "...", data: [...] }
        const raw = (response as any)?.data?.data ?? [];
        const ports = raw.map((port: any) => ({
          id: typeof port.id === 'number' ? port.id : Number(port.id),
          label: port.name || port.portName || String(port.id),
        }));
        setPortOptions(ports);
      } catch (err) {
        console.error("Error fetching ports:", err);
      }
    };

    fetchPorts();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);
  // ------------------------------------------------------------------------------------
  // Handle ADD
  // ------------------------------------------------------------------------------------
  const handleAddInternet = () => {
    setEditMode(false);
    setIsModalOpen(true);
    // Reset form
    internetForm.resetForm();
    setSelectedPortId(null);
  };

  // ------------------------------------------------------------------------------------
  // Handle EDIT
  // ------------------------------------------------------------------------------------
  const handleEditInternet = async (internet: Internet) => {
    setEditMode(true);
    setIsModalOpen(true);
    setIsEditLoading(true);
    setSelectedInternetId(typeof internet.id === 'number' ? internet.id : Number(internet.id));
    setSelectedInternet(null);
    internetForm.resetForm();

    try {
      const token = await getAccessTokenSilently();
      const response = await getInternetServiceById(token, internet.id);
      const raw = (response as any)?.data?.data ?? (response as any)?.data;

      if (raw) {
        // Shape the detail response with all fields
        const shapedData: Internet = {
          id: raw.id ?? internet.id,
          name: raw.name ?? internet.name,
          tag: Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : (internet.tag ?? []),
          isDraft: Boolean(raw.isDraft ?? internet.isDraft),
          subscriptionId: raw.subscriptionId ?? internet.subscriptionId,
          tenantNameDisplay: raw.kontrolEdgePort?.tenant?.nameDisplay ?? internet.tenantNameDisplay,
          // Detail response fields
          kontrolEdgePortId: raw.kontrolEdgePortId,
          mbps: raw.mbps,
          vlan: raw.vlan,
          automaticVlan: Boolean(raw.automaticVlan),
          redundancy: Boolean(raw.redundancy),
          countryCode: raw.countryCode,
          routing: raw.routing,
          terms: raw.terms,
          autoRenew: Boolean(raw.autoRenew),
          p2pAddressing: raw.p2pAddressing,
          bgpConfig: raw.bgpConfig,
          kontrolEdgeLagId: raw.kontrolEdgeLagId,
          latencyTargets: raw.latencyTargets,
          fwaasConfig: raw.fwaasConfig,
          createdAt: raw.createdAt,
          updatedAt: raw.updatedAt,
          odooMetadata: raw.odooMetadata,
          kontrolEdgePort: raw.kontrolEdgePort,
        };
        setSelectedInternet(shapedData);
        
        // Populate internetForm with fetched data
        internetForm.setFormData({
          id: typeof shapedData.id === 'number' ? shapedData.id : null,
          kontrolEdgePortId: typeof shapedData.kontrolEdgePortId === 'number' ? shapedData.kontrolEdgePortId : null,
          mbps: typeof shapedData.mbps === 'number' ? shapedData.mbps : null,
          vlan: typeof shapedData.vlan === 'number' ? shapedData.vlan : null,
          automaticVlan: shapedData.automaticVlan ?? null,
          redundancy: shapedData.redundancy ?? null,
          countryCode: shapedData.countryCode ?? "",
          routing: shapedData.routing ?? "",
          terms: typeof shapedData.terms === 'number' ? shapedData.terms : null,
          autoRenew: shapedData.autoRenew ?? null,
          subscriptionId: shapedData.subscriptionId ?? "",
          name: shapedData.name ?? "",
          tag: Array.isArray(shapedData.tag) ? shapedData.tag : [],
          // Every loaded key is kept: the PUT replaces both columns whole.
          p2pAddressing: p2pAddressingFromApi(shapedData.p2pAddressing),
          bgpConfig: bgpConfigFromApi(shapedData.bgpConfig),
          latencyTargets: shapedData.latencyTargets ?? null,
          isDraft: shapedData.isDraft ? 0 : 1, // Map boolean to number: true (Draft) -> 0, false (Active) -> 1
        });
        
        // Set port selection
        if (shapedData.kontrolEdgePortId) {
          setSelectedPortId(shapedData.kontrolEdgePortId);
        }
      }
    } catch (error) {
      console.error("Error fetching Internet service details for edit:", error);
      // Fallback to table data
      setSelectedInternet(internet);
      internetForm.resetForm();
    } finally {
      setIsEditLoading(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Handle VIEW
  // ------------------------------------------------------------------------------------
  const handleViewInternet = async (internet: Internet) => {
    setIsViewModalOpen(true);
    setIsViewLoading(true);
    setSelectedInternet(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await getInternetServiceById(token, internet.id);
      const raw = (response as any)?.data?.data ?? (response as any)?.data;

      if (raw) {
        // Shape the detail response with all fields
        const shapedData: Internet = {
          id: raw.id ?? internet.id,
          name: raw.name ?? internet.name,
          tag: Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : (internet.tag ?? []),
          isDraft: Boolean(raw.isDraft ?? internet.isDraft),
          subscriptionId: raw.subscriptionId ?? internet.subscriptionId,
          tenantNameDisplay: raw.kontrolEdgePort?.tenant?.nameDisplay ?? internet.tenantNameDisplay,
          // Detail response fields
          kontrolEdgePortId: raw.kontrolEdgePortId,
          mbps: raw.mbps,
          vlan: raw.vlan,
          automaticVlan: Boolean(raw.automaticVlan),
          redundancy: Boolean(raw.redundancy),
          countryCode: raw.countryCode,
          routing: raw.routing,
          terms: raw.terms,
          autoRenew: Boolean(raw.autoRenew),
          p2pAddressing: raw.p2pAddressing,
          bgpConfig: raw.bgpConfig,
          kontrolEdgeLagId: raw.kontrolEdgeLagId,
          latencyTargets: raw.latencyTargets,
          fwaasConfig: raw.fwaasConfig,
          createdAt: raw.createdAt,
          updatedAt: raw.updatedAt,
          odooMetadata: raw.odooMetadata,
          kontrolEdgePort: raw.kontrolEdgePort,
        };
        setSelectedInternet(shapedData);
      }
    } catch (error) {
      console.error("Error fetching Internet service details:", error);
      setSelectedInternet(internet);
    } finally {
      setIsViewLoading(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Handle DELETE
  // ------------------------------------------------------------------------------------
  const handleDeleteClick = (internet: Internet) => {
    setInternetToDelete(internet);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!internetToDelete) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await deleteInternetService(token, internetToDelete.id);

      setInternetServices((prev) =>
        prev.filter((item) => item.id !== internetToDelete.id)
      );
      setInternetToDelete(null);
      setIsAlertOpen(false);
    } catch (error) {
      console.error("Error deleting Internet service:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleCancelDelete = () => {
    setInternetToDelete(null);
    setIsAlertOpen(false);
  };

  // ------------------------------------------------------------------------------------
  // Handle SAVE (Update)
  // ------------------------------------------------------------------------------------
  const handleSaveInternet = async () => {
    if (!selectedInternetId) return;

    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();
      
      // Convert isDraft from number (0/1) to boolean (true/false)
      // 0 = Draft = true, 1 = Active = false
      const isDraftBoolean = internetForm.formData.isDraft === 0;

      // Build payload with all fields (same as create payload)
      // Include all fields so they're visible in the request, even if unchanged
      const payload: UpdateInternetPayload = {
        kontrolEdgePortId: internetForm.formData.kontrolEdgePortId ?? undefined,
        mbps: internetForm.formData.mbps ?? 0,
        vlan: internetForm.formData.vlan ?? undefined,
        automaticVlan: internetForm.formData.automaticVlan ?? undefined,
        redundancy: internetForm.formData.redundancy ?? undefined,
        countryCode: internetForm.formData.countryCode || undefined,
        routing: internetForm.formData.routing.trim() !== "" 
          ? internetForm.formData.routing 
          : undefined,
        terms: internetForm.formData.terms ?? undefined,
        autoRenew: internetForm.formData.autoRenew ?? undefined,
        p2pAddressing: p2pAddressingForPayload(internetForm.formData.p2pAddressing),
        bgpConfig: bgpConfigForPayload(internetForm.formData.bgpConfig),
        subscriptionId: internetForm.formData.subscriptionId || undefined,
        name: internetForm.formData.name,
        tag: internetForm.formData.tag || [],
        isDraft: isDraftBoolean,
        latencyTargets: internetForm.formData.latencyTargets || null,
      };

      await updateInternetService(token, selectedInternetId, payload);
      
      // Refresh the list
      const response = await getInternetServices(token);
      const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      const shapedData = shapeInternetData(raw);
      setInternetServices(shapedData);
      
      setIsModalOpen(false);
      setEditMode(false);
      setSelectedInternetId(null);
      setSelectedInternet(null);
      internetForm.resetForm();
      setSelectedPortId(null);
    } catch (error) {
      console.error("Error updating Internet service:", error);
    } finally {
      setIsSaving(false);
    }
  };



  // Handle port selection
  const handlePortSelect = (label: string) => {
    const port = portOptions.find((p) => p.label === label);
    if (port) {
      setSelectedPortId(port.id);
      internetForm.updateField("kontrolEdgePortId", port.id);
    }
  };

  const getPortLabelById = (id: number | null): string => {
    if (id === null) return "";
    const port = portOptions.find((p) => p.id === id);
    return port ? port.label : "";
  };

  // Check if create form is valid
  const isCreateFormValid = useMemo(() => {
    return (
      internetForm.formData.kontrolEdgePortId !== null &&
      internetForm.formData.mbps !== null &&
      internetForm.formData.vlan !== null &&
      internetForm.formData.automaticVlan !== null &&
      internetForm.formData.redundancy !== null &&
      internetForm.formData.countryCode.trim() !== "" &&
      internetForm.formData.routing.trim() !== "" &&
      internetForm.formData.terms !== null &&
      internetForm.formData.autoRenew !== null &&
      internetForm.formData.p2pAddressing.mask !== null &&
      internetForm.formData.p2pAddressing.peerIp.trim() !== "" &&
      internetForm.formData.p2pAddressing.gateway.trim() !== "" &&
      internetForm.formData.p2pAddressing.network.trim() !== "" &&
      internetForm.formData.subscriptionId.trim() !== "" &&
      internetForm.formData.name.trim() !== ""
    );
  }, [internetForm.formData]);

  // Handle create Internet service
  const handleCreateInternet = async () => {
    try {
      setIsCreating(true);
      const token = await getAccessTokenSilently();

      // Convert isDraft from number (0/1) to boolean (true/false)
      // 0 = Draft = true, 1 = Active = false
      const isDraftBoolean = internetForm.formData.isDraft === 0;

      // Build payload - P2P addressing is required
      const payload: AddInternetPayload = {
        kontrolEdgePortId: internetForm.formData.kontrolEdgePortId!,
        mbps: internetForm.formData.mbps!,
        vlan: internetForm.formData.vlan!,
        automaticVlan: internetForm.formData.automaticVlan!,
        redundancy: internetForm.formData.redundancy!,
        countryCode: internetForm.formData.countryCode,
        // routing and terms are not in the form UI, use defaults if not provided
        routing: internetForm.formData.routing.trim() !== "" 
          ? internetForm.formData.routing 
          : "static", // Default value
        terms: internetForm.formData.terms !== null 
          ? internetForm.formData.terms! 
          : 12, // Default value
        autoRenew: internetForm.formData.autoRenew!,
        // The create modal does not let an incomplete primary leg through.
        p2pAddressing: p2pAddressingForPayload(internetForm.formData.p2pAddressing),
        bgpConfig: bgpConfigForPayload(internetForm.formData.bgpConfig),
        subscriptionId: internetForm.formData.subscriptionId,
        name: internetForm.formData.name,
        tag: internetForm.formData.tag || [], // Tags are optional, default to empty array
        isDraft: isDraftBoolean, // Convert from number to boolean
      };

      await addInternetService(token, payload);

      // Refresh the list
      const response = await getInternetServices(token);
      const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      const shapedData = shapeInternetData(raw);
      setInternetServices(shapedData);

      // Close modal and reset form
      setIsModalOpen(false);
      internetForm.resetForm();
      setSelectedPortId(null);
    } catch (error) {
      console.error("Error creating Internet service:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const columns = [
    ...baseColumns,
    {
      header: "Actions",
      accessor: "name" as keyof Internet,
      renderCell: (row: Internet) => (
        <div className="flex items-center gap-2">
          {/* View Button */}
          <button
            className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              w-10 h-10
            "
            onClick={(e) => {
              e.stopPropagation();
              handleViewInternet(row);
            }}
            title="View details"
          >
            <IconEye className="w-5 h-5" />
          </button>

          {/* Edit Button */}
          <button
            className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              w-10 h-10
            "
            onClick={(e) => {
              e.stopPropagation();
              handleEditInternet(row);
            }}
            title="Edit"
          >
            <IconEdit className="w-9 h-9" />
          </button>

          {/* Delete Button */}
          <button
            className="
              flex items-center justify-center
              p-2
              border border-semantic-border-secondary
              rounded
              bg-red-50
              text-red-600
              hover:bg-red-100
              w-10 h-10
            "
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            title="Delete"
          >
            <IconDelete className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  // ------------------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------------------
  return (
    <GenericCard isCollapsible={false}>
      {/* Header */}
      <div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
        <div className="flex items-end justify-between">
          <div className="w-[520px] my-5">
            <GenericSearchbar
              placeholder="Find by attribute or value"
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
            />
          </div>
          <div className="mb-10">
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>Add new Internet service</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={handleAddInternet}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-5 mb-5">
      {/* The table's own slot in skeleton. The overlay this replaces covered
          the rail and the views bar for the whole wait (CTX-1028). */}
      {isFetching ? (
        <TableSkeleton columns={columns.length} rows={5} />
      ) : (
        <GenericTable
            columns={columns}
            data={filteredData}
            initialRowsPerPage={filteredData.length > 0 ? Math.max(10, filteredData.length) : 10}
            rowsPerPageOptions={[10, 25, 50, 100, filteredData.length > 100 ? filteredData.length : undefined].filter(Boolean) as number[]}
            tableClassName="bg-[var(--semantic-bg-primary)]"
            width="100%"
            maxTableBodyHeight={
              filteredData.length > 0 
                ? `${Math.min(Math.max(500, filteredData.length * 45 + 60), 1000)}px`
                : "500px"
            }
          />
      )}
      </div>

      {/* ====================== Modal Add / Edit ====================== */}
      {!editMode && (
        <InternetCreateModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            internetForm.resetForm();
            setSelectedPortId(null);
          }}
          onSave={handleCreateInternet}
          portOptions={portOptions}
          selectedPortId={selectedPortId}
          getPortLabelById={getPortLabelById}
          handlePortSelect={handlePortSelect}
          internetForm={internetForm}
          isFormValid={isCreateFormValid}
          isCreating={isCreating}
        />
      )}

      {editMode && (
        <InternetEditModal
          isOpen={isModalOpen}
          isLoading={isEditLoading}
          onClose={() => {
            setIsModalOpen(false);
            setEditMode(false);
            setSelectedInternetId(null);
            setSelectedInternet(null);
            internetForm.resetForm();
            setSelectedPortId(null);
          }}
          onSave={handleSaveInternet}
          portOptions={portOptions}
          selectedPortId={selectedPortId}
          getPortLabelById={getPortLabelById}
          handlePortSelect={handlePortSelect}
          internetForm={internetForm}
          internet={selectedInternet}
          isSaving={isSaving}
        />
      )}

      {/* ====================== Modal View (read-only) ====================== */}
      <InternetViewModal
        isOpen={isViewModalOpen}
        isLoading={isViewLoading}
        internet={selectedInternet}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* ====================== ALERT: confirm Delete ====================== */}
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the Internet service from the inventory."
        cancelLabel="Cancel"
        confirmLabel="Delete"
        iconWidthClass="w-20 h-12"
      />
    </GenericCard>
  );
}

